import { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useParams, useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import IconEdit from '../../components/icons/IconEdit';
import IconDelete from '../../components/icons/IconDelete';
import PlannedEventCreateModal from './PlannedEventCreateModal';
import PlannedEventDetailModal from './PlannedEventDetailModal';

const url = '/api/student/plannedevents';

// some legacy planned-event text/date columns store the literal string "null"
const cleanText = (val) => (val === 'null' || val === 'undefined' || val === null || val === undefined) ? '' : val;

// "2 год 30 хв" style relative countdown to a future DateTime - same shape
// as StudentCard.jsx's own formatCountdown (duplicated locally rather than
// shared, matching cleanText above).
const formatRelativeCountdown = (targetDt, now, lang) => {
   const totalMinutes = Math.max(0, Math.round(targetDt.diff(now, 'minutes').minutes));
   const days = Math.floor(totalMinutes / 1440);
   const hours = Math.floor((totalMinutes % 1440) / 60);
   const minutes = totalMinutes % 60;
   const parts = [];
   if(days > 0) parts.push(days + ' ' + lang.daysShort);
   if(days > 0 || hours > 0) parts.push(hours + ' ' + lang.hoursShort);
   parts.push(minutes + ' ' + lang.minutesShort);
   return parts.join(' ');
}

const nextWeekdayOccurrence = (weekDay, timeStr, now) => {
   const parts = String(timeStr).split(':');
   const h = Number(parts[0]), m = Number(parts[1]);
   if(Number.isNaN(h) || Number.isNaN(m)) return null;
   let candidate = now.set({hour:h, minute:m, second:0, millisecond:0});
   const diff = (weekDay - now.weekday + 7) % 7;
   candidate = candidate.plus({days: diff});
   if(candidate <= now) candidate = candidate.plus({days:7});
   return candidate;
}

// resolve a single schedule entry (FIXED/AFTER_LESSON/BEFORE_LESSON) to its
// next concrete firing DateTime. BEFORE_LESSON always counts back from the
// NEXT upcoming lesson's start. AFTER_LESSON is two-step, matching the
// backend cron's own re-anchoring behaviour: if the LAST completed lesson's
// own end+offset target hasn't elapsed yet (e.g. it ended 30min ago and the
// offset is 1h - that target is still 30min away), that's the real next
// firing; only once that window has already passed does it fall through to
// the NEXT upcoming lesson's end+offset (the lesson whose later completion
// will give the cron a new anchor). No lesson at all - past-with-a-still-
// open window, or future - means no predictable next run.
const resolveEntryNextRun = (d, now, nextLessonDt, nextLessonEndDt, lastLessonEndDt) => {
   if(d.scheduleMode === 'AFTER_LESSON') {
      if(lastLessonEndDt) {
         const fromLast = lastLessonEndDt.plus({hours: d.offsetHours || 0, minutes: d.offsetMinutes || 0});
         if(fromLast > now) return fromLast;
      }
      if(!nextLessonEndDt) return null;
      const fromNext = nextLessonEndDt.plus({hours: d.offsetHours || 0, minutes: d.offsetMinutes || 0});
      return fromNext > now ? fromNext : null;
   }
   if(d.scheduleMode === 'BEFORE_LESSON') {
      if(!nextLessonDt) return null;
      const target = nextLessonDt.minus({hours: d.offsetHours || 0, minutes: d.offsetMinutes || 0});
      return target > now ? target : null;
   }
   return nextWeekdayOccurrence(d.weekDay, d.time, now);
}

const PlannedEvents = () => {
   const { id } = useParams();
   const navigate = useNavigate();

   const [events, setEvents] = useState([]);
   const [schedules, setSchedules] = useState({});
   const [nextLessonDt, setNextLessonDt] = useState(null);
   const [nextLessonEndDt, setNextLessonEndDt] = useState(null);
   const [lastLessonEndDt, setLastLessonEndDt] = useState(null);
   const [search, setSearch] = useState('');
   const [typeFilter, setTypeFilter] = useState('');
   const [activeFilter, setActiveFilter] = useState('');
   const [cycledFilter, setCycledFilter] = useState('');
   const [refresh, setRefresh] = useState(0);
   const [createOpen, setCreateOpen] = useState(false);
   const [modalState, setModalState] = useState('none');
   const [detailId, setDetailId] = useState('');
   const [togglingId, setTogglingId] = useState(null);

   const refreshList = () => setRefresh(prev => prev + 1);

   useEffect(() => {
      axios.get(url + '?student=' + id, { withCredentials: true })
         .then(response => setEvents(Array.isArray(response.data) ? response.data : []))
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
   }, [id, refresh, navigate]);

   // The student's next-upcoming lesson (start AND end) and last-completed
   // lesson (end) - the anchors AFTER_LESSON/BEFORE_LESSON schedule entries
   // resolve against. AFTER_LESSON checks the last-completed lesson FIRST
   // (its own end+offset target might still be ahead of now - see
   // resolveEntryNextRun) and only falls back to the next upcoming lesson
   // once that window has passed; BEFORE_LESSON only ever needs the next
   // lesson's start. Same /api/events?student=X&dateFrom|dateTo=today
   // pattern StudentCard.jsx's header pills already use.
   useEffect(() => {
      if(!id) return;
      const today = DateTime.now().toFormat('yyyy-MM-dd');
      axios.get('/api/events?student=' + id + '&dateFrom=' + today, { withCredentials: true })
         .then(response => {
            const list = Array.isArray(response.data) ? response.data : [];
            const now = DateTime.now();
            let closestStart = null;
            let closestEnd = null;
            list.forEach(ev => {
               if(!ev.date || !ev.start) return;
               const startDt = DateTime.fromFormat(ev.date + ' ' + ev.start, 'yyyy-MM-dd HH:mm');
               if(!startDt.isValid || startDt <= now) return;
               if(!closestStart || startDt < closestStart) {
                  closestStart = startDt;
                  const endDt = ev.end ? DateTime.fromFormat(ev.date + ' ' + ev.end, 'yyyy-MM-dd HH:mm') : null;
                  closestEnd = (endDt && endDt.isValid) ? endDt : startDt;
               }
            });
            setNextLessonDt(closestStart);
            setNextLessonEndDt(closestEnd);
         })
         .catch(() => { setNextLessonDt(null); setNextLessonEndDt(null); });
      axios.get('/api/events?student=' + id + '&dateTo=' + today, { withCredentials: true })
         .then(response => {
            const list = Array.isArray(response.data) ? response.data : [];
            const now = DateTime.now();
            let closest = null;
            list.forEach(ev => {
               if(!ev.date || !(ev.end || ev.start)) return;
               const dt = DateTime.fromFormat(ev.date + ' ' + (ev.end || ev.start), 'yyyy-MM-dd HH:mm');
               if(!dt.isValid || dt > now) return;
               if(!closest || dt > closest) closest = dt;
            });
            setLastLessonEndDt(closest);
         })
         .catch(() => setLastLessonEndDt(null));
   }, [id]);

   useEffect(() => {
      const cycled = events.filter(ev => ev.isCycled === 1);
      if(cycled.length === 0) {
         setSchedules({});
         return;
      }
      Promise.all(cycled.map(ev =>
         axios.get(url + '/weekdays/' + ev.id, { withCredentials: true })
            .then(response => ({ id: ev.id, days: Array.isArray(response.data) ? response.data : [] }))
            .catch(() => ({ id: ev.id, days: [] }))
      )).then(results => {
         const map = {};
         results.forEach(r => { map[r.id] = r.days; });
         setSchedules(map);
      });
   }, [events]);

   const lang = language[language.lang];
   const plang = lang.plannedEvents;

   const typeName = (type) => {
      if(!type) return '';
      return lang[type.name] || type.name;
   }

   const filteredEvents = events.filter(ev => {
      if(search.trim() && !cleanText(ev.message).toLowerCase().includes(search.trim().toLowerCase())) return false;
      if(typeFilter && (!ev.planedStudentEventType || String(ev.planedStudentEventType.id) !== String(typeFilter))) return false;
      if(activeFilter && String(ev.isActive) !== String(activeFilter)) return false;
      if(cycledFilter === 1 && ev.isCycled !== 1) return false;
      if(cycledFilter === 2 && ev.isCycled === 1) return false;
      return true;
   });

   const typeOptions = [];
   events.forEach(ev => {
      if(ev.planedStudentEventType && !typeOptions.some(t => t.id === ev.planedStudentEventType.id)) {
         typeOptions.push(ev.planedStudentEventType);
      }
   });

   const computeNextRun = () => {
      const now = DateTime.now();
      let best = null;
      events.forEach(ev => {
         if(ev.isActive !== 1) return;
         if(ev.isCycled === 1) {
            const days = schedules[ev.id] || [];
            days.forEach(d => {
               const candidate = resolveEntryNextRun(d, now, nextLessonDt, nextLessonEndDt, lastLessonEndDt);
               if(candidate && (!best || candidate < best)) best = candidate;
            });
         } else {
            const clean = cleanText(ev.dateTime);
            if(!clean) return;
            const dt = DateTime.fromISO(clean);
            if(dt.isValid && dt > now && (!best || dt < best)) best = dt;
         }
      });
      return best;
   }
   const nextRun = computeNextRun();
   const nextRunLabel = nextRun ? (lang.weekDaysShort[nextRun.weekday - 1] + ' ' + nextRun.toFormat('dd.MM HH:mm')) : plang.noNextRun;

   // "+30 хв після заняття" / "-15 хв до заняття" - same short relative-offset
   // labels as PlannedEventDetailModal.jsx's chip list (duplicated rather than
   // shared, matching cleanText above - this file already keeps its own copy
   // of that small a helper rather than importing one).
   const formatOffsetShort = (hours, minutes) => {
      const parts = [];
      if(hours) parts.push(hours + ' ' + lang.hoursShort);
      if(minutes) parts.push(minutes + ' ' + lang.minutesShort);
      if(parts.length === 0) return plang.immediatelyAfterLesson;
      return '+' + parts.join(' ') + ' ' + plang.afterLessonShort;
   }
   const formatOffsetBeforeShort = (hours, minutes) => {
      const parts = [];
      if(hours) parts.push(hours + ' ' + lang.hoursShort);
      if(minutes) parts.push(minutes + ' ' + lang.minutesShort);
      if(parts.length === 0) return plang.immediatelyBeforeLesson;
      return '-' + parts.join(' ') + ' ' + plang.beforeLessonShort;
   }
   const formatEntryLabel = (d) => {
      if(d.scheduleMode === 'AFTER_LESSON') return formatOffsetShort(d.offsetHours, d.offsetMinutes);
      if(d.scheduleMode === 'BEFORE_LESSON') return formatOffsetBeforeShort(d.offsetHours, d.offsetMinutes);
      return lang.weekDaysShort[d.weekDay - 1] + ' ' + String(d.time).slice(0,5);
   }

   // picks the most specific reason none of this row's entries resolved to a
   // future run. Both offset modes depend on the same next-upcoming-lesson
   // anchor, so "no future lesson at all" is one universal message - the one
   // remaining distinct case is BEFORE_LESSON with a next lesson that's
   // already too close for its offset to still land in the future.
   const resolveNoRunMessage = (days, nextLessonDt, nextLessonEndDt) => {
      const hasBefore = days.some(d => d.scheduleMode === 'BEFORE_LESSON');
      const hasAfter = days.some(d => d.scheduleMode === 'AFTER_LESSON');
      if((hasBefore && !nextLessonDt) || (hasAfter && !nextLessonEndDt)) return plang.noUpcomingLessons;
      if(hasBefore) return plang.lessonTooSoon;
      return plang.noUpcomingRun;
   }

   const renderSchedule = (ev) => {
      if(ev.isCycled === 1) {
         const days = (schedules[ev.id] || []).slice().sort((a,b) => a.weekDay - b.weekDay || (a.time > b.time ? 1 : -1));
         if(days.length === 0) {
            return <span className='student-lessons-badge unset'>{plang.noSchedule}</span>;
         }
         return (
            <div className='planned-schedule-chips'>
               {days.map(d => (
                  <span className='student-lessons-badge' key={d.id}>{formatEntryLabel(d)}</span>
               ))}
            </div>
         );
      }
      const clean = cleanText(ev.dateTime);
      if(!clean) return '—';
      const dt = DateTime.fromISO(clean);
      return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '—';
   }

   const renderNextRun = (ev) => {
      if(ev.isCycled !== 1) return '—';
      const days = schedules[ev.id] || [];
      if(days.length === 0) return '—';
      const now = DateTime.now();
      let best = null;
      days.forEach(d => {
         const candidate = resolveEntryNextRun(d, now, nextLessonDt, nextLessonEndDt, lastLessonEndDt);
         if(candidate && (!best || candidate < best)) best = candidate;
      });
      if(!best) return <span className='planned-row-next-run-empty'>{resolveNoRunMessage(days, nextLessonDt, nextLessonEndDt)}</span>;
      return (
         <div className='planned-next-run-value'>
            <span>{plang.inPrefix} {formatRelativeCountdown(best, now, lang)}</span>
            <span className='planned-next-run-absolute'>{lang.weekDaysShort[best.weekday - 1] + ' ' + best.toFormat('dd.MM HH:mm')}</span>
         </div>
      );
   }

   const handleToggleActive = (e, ev) => {
      e.stopPropagation();
      if(togglingId) return;
      const newValue = ev.isActive === 1 ? 2 : 1;
      setTogglingId(ev.id);
      axios.put(url + '/' + ev.id, JSON.stringify({ isActive: String(newValue) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         refreshList();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setTogglingId(null));
   }

   const handleDelete = (e, ev) => {
      e.stopPropagation();
      if(!window.confirm(plang.deleteConfirm)) return;
      axios.delete(url + '/' + ev.id, { withCredentials: true })
         .then(() => refreshList())
         .catch(err => console.log(err));
   }

   const openDetail = (evId) => { setDetailId(evId); setModalState('detail'); }
   const closeDetail = () => { setModalState('none'); setDetailId(''); }

   const onCreated = (newId, isCycled) => {
      setCreateOpen(false);
      refreshList();
      if(isCycled && newId) openDetail(newId);
   }
   const onDetailSaved = () => {
      refreshList();
   }
   const onDetailDeleted = () => {
      closeDetail();
      refreshList();
   }

   return (
      <div className='student-lessons'>
         <div className='student-lessons-toolbar'>
            <div className='student-lessons-stats'>
               <span className='student-lessons-stat-pill'><b>{events.length}</b><small>{plang.total}</small></span>
               <span className='student-lessons-stat-pill'><b>{events.filter(e => e.isActive === 1).length}</b><small>{lang.active}</small></span>
               <span className='student-lessons-stat-pill'><b>{events.filter(e => e.isCycled === 1).length}</b><small>{plang.recurring}</small></span>
               <span className='student-lessons-stat-pill'><b>{nextRunLabel}</b><small>{plang.nextRun}</small></span>
            </div>
            <div className='student-lessons-add-btn' onClick={() => setCreateOpen(true)} title={lang['Plan some event']}>
               <IconAdd />
            </div>
         </div>

         <FilterBar
            search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }}
            filters={[
               {key:'type', type:'select', label: plang.type, options: typeOptions, value: typeFilter, onChange: setTypeFilter, numeric:true},
               {key:'isCycled', type:'select', label: lang.isCycled, options:[{id:1,name:'Yes'},{id:2,name:'No'}], value: cycledFilter, onChange: setCycledFilter, numeric:true},
               {key:'isActive', type:'select', label: lang.isActive, options:[{id:1,name:'Yes'},{id:2,name:'No'}], value: activeFilter, onChange: setActiveFilter, numeric:true},
            ]}
            resultCount={filteredEvents.length} />

         <div className='student-lessons-table-wrap'>
            <table className='student-lessons-table'>
               <thead>
                  <tr>
                     <th>{plang.type}</th>
                     <th>{plang.when}</th>
                     <th>{plang.expectedRun}</th>
                     <th>{lang.message}</th>
                     <th>{lang.isActive}</th>
                     <th></th>
                  </tr>
               </thead>
               <tbody>
                  {filteredEvents.map(ev => (
                     <tr key={ev.id} className='student-lessons-row' onClick={() => openDetail(ev.id)}>
                        <td>{typeName(ev.planedStudentEventType)}</td>
                        <td>{renderSchedule(ev)}</td>
                        <td className='planned-next-run-cell'>{renderNextRun(ev)}</td>
                        <td className='student-lessons-text-cell'>{cleanText(ev.message) || '—'}</td>
                        <td onClick={e => e.stopPropagation()}>
                           <span
                              className={'student-lessons-badge toggle-clickable' + (ev.isActive === 1 ? ' on' : ' off-explicit') + (togglingId === ev.id ? ' toggle-pending' : '')}
                              onClick={(e) => handleToggleActive(e, ev)}>
                              <i className='student-lessons-badge-dot'></i>{ev.isActive === 1 ? lang.active : lang.inactive}
                           </span>
                        </td>
                        <td className='student-lessons-actions-cell' onClick={e => e.stopPropagation()}>
                           <div className='student-lessons-icon-btn' onClick={() => openDetail(ev.id)}>
                              <IconEdit style={{fill:'#7ec2e8', width:14, height:14, margin:0}} />
                           </div>
                           <div className='student-lessons-icon-btn' onClick={(e) => handleDelete(e, ev)}>
                              <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
                           </div>
                        </td>
                     </tr>
                  ))}
                  {filteredEvents.length === 0 ? (
                     <tr className='student-lessons-empty-row'><td colSpan={6}>{lang['Empty list']}</td></tr>
                  ) : ''}
               </tbody>
            </table>
         </div>

         <PlannedEventCreateModal isOpen={createOpen} studentId={id} onClose={() => setCreateOpen(false)} onCreated={onCreated} />

         <PlannedEventDetailModal isOpen={modalState === 'detail'} eventId={detailId}
            onClose={closeDetail} onSaved={onDetailSaved} onDeleted={onDetailDeleted} />
      </div>
   );
}

export default PlannedEvents;
